// src/encounter/lib/step-completion.ts
//
// Antonin, 16 Oct (rule C): a step's ✓ in the default frame's step bar is DERIVED from what the note holds, every render, so it
// survives a reload and reopening a Draft; visiting a step, or pressing Next, completes nothing by itself (authority §4 rule 8).
// It reuses frame v2's documented-content rule (`structured-note/section-completion.ts`) step by step:
//   HPI      a chief complaint AND no blank required item
//   Risk     Fall AND Pain both answered, and no blank required item
//   ROS, PE  a finding in the section (All Neg counts) and no blank required item
//   A/P      at least one diagnosis (not for allied health) AND an order or an article; ALWAYS ✓ once the PROVIDER has signed (19 Oct)
//   Doc, Wrap-up, the nurse / allied Plan: never ✓ for now (no signal in this view reads as documentation)
// And the "needs attention" marker: a step with blank required items (or an A/P with no diagnosis) that has been visited or
// already holds an answer — never on a fresh encounter, so it is not all warnings. The nurse sees no required-item marker.
import type { Questionnaire, QuestionnaireResponse } from '@scripsteam/scrips-signal-ds';
import type { PaStepKey } from './encounter-steps';
import { isQuestionnaireStep, questionnaireForStep } from './questionnaire-groups';
import { unansweredRequiredItemGaps } from './required-items';
import { stepDocumented, type PlanContent } from '../structured-note/section-completion';
import { answerMap, openRequired } from '../structured-note/note-adapter';

export interface StepCompletionInput {
  navSteps: readonly PaStepKey[];
  questionnaire?: Questionnaire;
  response: QuestionnaireResponse;
  plan?: PlanContent;
  /** A chief complaint is recorded (the HPI step's first condition). */
  chiefComplaintPresent: boolean;
  /** A Fall / Pain assessment is recorded (Risk's own store, outside the questionnaire response). */
  riskAnswered: boolean;
  /** A/P needs a diagnosis (physician); allied health is exempt. */
  diagnosisRequired: boolean;
  /** Risk's Fall and Pain recorded separately (Risk's own store): Risk is ✓ when both are. */
  fallAnswered: boolean;
  painAnswered: boolean;
  /** The PROVIDER has signed (a nurse-only sign is not this). Signing is what completes Assessment & plan (19 Oct, Antonin). */
  providerSigned?: boolean;
}

function hasBlank(step: PaStepKey, input: StepCompletionInput): boolean {
  const { questionnaire, response } = input;
  return isQuestionnaireStep(step) && !!questionnaire && openRequired(questionnaireForStep(questionnaire, step), answerMap(response)).length > 0;
}

export function stepHoldsAnswer(step: PaStepKey, input: StepCompletionInput): boolean {
  return holdsAnswer(step, input);
}

function holdsAnswer(step: PaStepKey, input: StepCompletionInput): boolean {
  const { questionnaire, response, plan } = input;
  if (step === 'risk' && input.riskAnswered) return true;
  if (step === 'ap') return (plan?.diagnoses?.length ?? 0) > 0 || (plan?.orders?.length ?? 0) > 0 || (plan?.articles?.length ?? 0) > 0;
  return stepDocumented(step, { questionnaire, response, plan });
}

/** The ✓ rules (16 Oct, Antonin: "put in general"; clinical rules to confirm with Samer / Andrew). HPI: chief complaint and no blank required item. Risk: Fall AND Pain both answered (and no blank
 *  required item). ROS / PE: a finding (All Neg counts) and no blank required item. A/P as before. Doc: never ✓ (the view reads no
 *  document count: a gap). Wrap-up: never derived. */
function stepDone(step: PaStepKey, input: StepCompletionInput): boolean {
  const { plan } = input;
  switch (step) {
    case 'hpi':
      return input.chiefComplaintPresent && !hasBlank(step, input);
    case 'risk':
      return !!input.fallAnswered && !!input.painAnswered && !hasBlank(step, input);
    case 'ros':
    case 'pe':
      return holdsAnswer(step, input) && !hasBlank(step, input);
    case 'ap': {
      // After the provider sign the cell is complete: signing is what completes it. Before the sign the rule is as before: a
      // diagnosis (physician) AND an order or an article.
      if (input.providerSigned) return true;
      const diagnosed = !input.diagnosisRequired || (plan?.diagnoses?.length ?? 0) > 0;
      return diagnosed && ((plan?.orders?.length ?? 0) > 0 || (plan?.articles?.length ?? 0) > 0);
    }
    default:
      return false;
  }
}

/** The steps that show ✓. */
export function derivedCompletedSteps(input: StepCompletionInput): PaStepKey[] {
  return input.navSteps.filter((step) => {
    return stepDone(step, input);
  });
}

/** Steps that need attention, with up to three of what is missing. Only a step that was visited or holds an answer. */
export function stepsNeedingAttention(
  input: StepCompletionInput & { visited: ReadonlySet<PaStepKey>; requiredMarkers: boolean },
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  const done = new Set(derivedCompletedSteps(input));
  const gaps = input.requiredMarkers ? unansweredRequiredItemGaps(input.questionnaire, input.response, input.navSteps) : [];
  for (const step of input.navSteps) {
    if (done.has(step)) continue;
    if (!input.visited.has(step) && !holdsAnswer(step, input)) continue;
    const missing: string[] = [];
    if (step === 'ap') {
      if (input.diagnosisRequired && (input.plan?.diagnoses?.length ?? 0) === 0) missing.push('A diagnosis');
    } else {
      for (const g of gaps) if (g.step === step) missing.push(g.item.text?.trim() || 'A required item');
    }
    if (missing.length > 0) out[step] = missing.slice(0, 3);
  }
  return out;
}
